import { ref, computed } from 'vue';
import billingAPI from '../api/omni/billing';
import { useAlert } from 'dashboard/composables';

export function useOmniBilling() {
  const subscription = ref(null);
  const currentPlan = ref(null);
  const plans = ref([]);
  const payments = ref([]);
  const loading = ref(false);
  const paymentsMeta = ref({ page: 1, per_page: 20, total: 0 });

  const hasActiveSubscription = computed(() => {
    return subscription.value?.status === 'active' || subscription.value?.status === 'trialing';
  });

  const canUseService = computed(() => {
    if (!subscription.value) return false;
    return ['active', 'trialing', 'past_due'].includes(subscription.value.status);
  });

  const isTrialing = computed(() => {
    return subscription.value?.status === 'trialing';
  });

  const isCanceled = computed(() => {
    return subscription.value?.canceled_at !== null;
  });

  const daysRemaining = computed(() => {
    return subscription.value?.days_remaining || 0;
  });

  const trialDaysRemaining = computed(() => {
    return subscription.value?.trial_days_remaining || 0;
  });

  const fetchBillingInfo = async () => {
    loading.value = true;
    try {
      const response = await billingAPI.get();
      subscription.value = response.data.subscription;
      currentPlan.value = response.data.plan;
    } catch (error) {
      useAlert(error.message || 'Error fetching billing info');
    } finally {
      loading.value = false;
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await billingAPI.getPlans();
      plans.value = response.data || [];
    } catch (error) {
      useAlert(error.message || 'Error fetching plans');
    }
  };

  const fetchPayments = async (page = 1) => {
    try {
      const response = await billingAPI.getPayments({ page });
      payments.value = response.data.payments || [];
      paymentsMeta.value = response.data.meta || paymentsMeta.value;
    } catch (error) {
      useAlert(error.message || 'Error fetching payments');
    }
  };

  // Create combined checkout for plan + addons
  // addons: Array of { id: number, quantity: number }
  const createCheckout = async (
    planId,
    billingCycle = 'monthly',
    addons = []
  ) => {
    loading.value = true;
    try {
      const response = await billingAPI.createCheckout(
        planId,
        billingCycle,
        addons
      );
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      useAlert(
        error.response?.data?.error || 'Error creating checkout session'
      );
      loading.value = false;
      throw error;
    }
  };

  // Update existing subscription (add/remove addons, change plan)
  const updateSubscription = async (
    planId = null,
    addons = null,
    billingCycle = null
  ) => {
    loading.value = true;
    try {
      const response = await billingAPI.updateSubscription(
        planId,
        addons,
        billingCycle
      );
      subscription.value = response.data.subscription;
      useAlert(response.data.message, 'success');
    } catch (error) {
      useAlert(
        error.response?.data?.error || 'Error updating subscription'
      );
      throw error;
    } finally {
      loading.value = false;
    }
  };

  // Upgrade to a new plan while preserving existing addons
  // This modifies the existing Stripe subscription instead of creating a new one
  const upgradePlan = async (planId, billingCycle = null) => {
    loading.value = true;
    try {
      const response = await billingAPI.upgradePlan(planId, billingCycle);
      subscription.value = response.data.subscription;
      useAlert(response.data.message, 'success');
      return response.data;
    } catch (error) {
      useAlert(
        error.response?.data?.error || 'Erro ao atualizar o plano'
      );
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const openPortal = async () => {
    loading.value = true;
    try {
      const response = await billingAPI.createPortal();
      // Redirect to Stripe Portal
      window.location.href = response.data.portal_url;
    } catch (error) {
      useAlert(error.response?.data?.error || 'Error opening billing portal');
      loading.value = false;
      throw error;
    }
  };

  const cancelSubscription = async (atPeriodEnd = true) => {
    loading.value = true;
    try {
      const response = await billingAPI.cancelSubscription(atPeriodEnd);
      subscription.value = response.data.subscription;
      useAlert(response.data.message, 'success');
    } catch (error) {
      useAlert(error.response?.data?.error || 'Error canceling subscription');
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const resumeSubscription = async () => {
    loading.value = true;
    try {
      const response = await billingAPI.resumeSubscription();
      subscription.value = response.data.subscription;
      useAlert(response.data.message, 'success');
    } catch (error) {
      useAlert(error.response?.data?.error || 'Error resuming subscription');
      throw error;
    } finally {
      loading.value = false;
    }
  };

  // Plan enforcement - exceeding resources management
  const exceedingResources = ref(null);
  const enforcementPending = ref(false);

  const fetchExceedingResources = async () => {
    try {
      const response = await billingAPI.getExceedingResources();
      exceedingResources.value = response.data;
      enforcementPending.value = response.data.enforcement_pending;
      return response.data;
    } catch (error) {
      console.error('Error fetching exceeding resources:', error);
      throw error;
    }
  };

  const enforceLimits = async (selections) => {
    loading.value = true;
    try {
      const response = await billingAPI.enforceLimits(selections);
      enforcementPending.value = response.data.enforcement_pending;
      useAlert(response.data.message, 'success');
      return response.data;
    } catch (error) {
      useAlert(error.response?.data?.error || 'Erro ao aplicar limites');
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const autoEnforceLimits = async () => {
    loading.value = true;
    try {
      const response = await billingAPI.autoEnforceLimits();
      enforcementPending.value = response.data.enforcement_pending;
      useAlert(response.data.message, 'success');
      return response.data;
    } catch (error) {
      useAlert(error.response?.data?.error || 'Erro ao aplicar limites');
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const reenableResources = async () => {
    loading.value = true;
    try {
      const response = await billingAPI.reenableResources();
      enforcementPending.value = false;
      useAlert(response.data.message, 'success');
      return response.data;
    } catch (error) {
      useAlert(error.response?.data?.error || 'Erro ao reabilitar recursos');
      throw error;
    } finally {
      loading.value = false;
    }
  };

  return {
    subscription,
    currentPlan,
    plans,
    payments,
    paymentsMeta,
    loading,
    hasActiveSubscription,
    canUseService,
    isTrialing,
    isCanceled,
    daysRemaining,
    trialDaysRemaining,
    exceedingResources,
    enforcementPending,
    fetchBillingInfo,
    fetchPlans,
    fetchPayments,
    createCheckout,
    updateSubscription,
    upgradePlan,
    openPortal,
    cancelSubscription,
    resumeSubscription,
    fetchExceedingResources,
    enforceLimits,
    autoEnforceLimits,
    reenableResources,
  };
}

